import { isLiteTemplate } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Anchor, Badge, Button, Code, Collapse, Group, Stack, Switch, Text, TextInput, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import {
  IconAlertTriangle,
  IconArrowsJoin,
  IconBrandTeams,
  IconClock,
  IconFilter,
  IconMicrophone,
  IconPlayerRecord,
  IconSearch,
} from "@tabler/icons-react"
import { useState } from "react"
import { EmptyState } from "../../../../components/EmptyState"
import { MeetingCard } from "../../../../components/MeetingCard"
import { MeetingHeroLive } from "../../../../components/MeetingHeroLive"
import { minutesSince } from "../../../../components/MeetingHeroLive/helpers"
import { MeetingListRow } from "../../../../components/MeetingListRow"
import { CardGridSkeleton, PageHeaderSkeleton } from "../../../../components/PageSkeletons"
import { VoxLiveHero } from "../../../../components/VoxLiveHero"
import { VoxPageTitle } from "../../../../components/VoxPageTitle"
import { VoxPillButton } from "../../../../components/VoxPillButton"
import { formatShortDate } from "../../../../lib/format-date"
import { displayMeetingTitle } from "../../../../lib/meeting-title"
import { getLabels } from "../../../../lib/product-labels"
import type { IMeetingListItem } from "../../../../lib/types/meeting"
import {
  DASHBOARD_LOAD_MORE_SENTINEL_ID,
  DASHBOARD_SEARCH_INPUT_ID,
  type IViewProps,
  type TGroupMode,
  type TListLayout,
  type TRecordingModeFilter,
} from "./interfaces"
import classes from "./styles.module.css"

const GROUPS: { id: TGroupMode; labelKey: ReturnType<typeof msg> }[] = [
  { id: "date", labelKey: msg`By date` },
  { id: "people", labelKey: msg`By people` },
]

const RECORDING_FILTERS: { id: TRecordingModeFilter; labelKey: ReturnType<typeof msg> }[] = [
  { id: "all", labelKey: msg`All` },
  { id: "full", labelKey: msg`Full` },
  { id: "lite", labelKey: msg`Lite` },
  { id: "error", labelKey: msg`Errors` },
]

/**
 * Collapsible raw-error disclosure for the load-error state. The friendly
 * recovery sentence is the headline; the developer-facing detail (a fetch
 * failure, a backend envelope) stays tucked behind a "Details" toggle so it
 * never reads as the primary message on the first screen a customer sees.
 */
const ErrorDetails = ({ detail }: { detail: string }): React.JSX.Element => {
  const [open, { toggle }] = useDisclosure(false)
  return (
    <Stack gap={4} align="center">
      <Anchor component="button" type="button" size="xs" c="dimmed" onClick={toggle}>
        {open ? <Trans>Hide details</Trans> : <Trans>Details</Trans>}
      </Anchor>
      <Collapse in={open}>
        <Code block>{detail}</Code>
      </Collapse>
    </Stack>
  )
}

/**
 * Meeting list / dashboard — pure presentation. The smart wrapper owns the
 * meetings + action-items queries, the SSE live-update subscription, the live
 * hero freshness logic, grouping, mutations, and the card-triggered modals; this
 * view renders the header, the live hero, the grouped card grid, and the empty
 * states. The smart RecurringSeriesCard and the modals are supplied/rendered by
 * the wrapper.
 */
export const MeetingListView = ({
  product,
  variant = "full",
  isStarting = false,
  totalCount,
  firstName,
  monthCount,
  totalMinutes,
  filteredIsEmpty,
  sections,
  filteredMeetings,
  processingById,
  live,
  connected,
  isLoading,
  error,
  search,
  onSearchChange,
  momentsActive,
  momentsSection,
  filtersContent,
  activeFilterCount,
  group,
  onGroupChange,
  recordingModeFilter = "all",
  onRecordingModeFilterChange,
  listLayout = "cards",
  onListLayoutChange,
  onRetryAllFailed,
  onDeleteAllFailed,
  retryAllBusy = false,
  showHidden,
  onShowHiddenChange,
  isOrgAdmin,
  userEmail,
  actionsCount,
  onCardAction,
  onNewMeeting,
  onMerge,
  onOpenLive,
  onRetry,
  recurringSeriesCard,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: IViewProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const labels = getLabels(i18n, product)
  const [filtersOpen, filtersControl] = useDisclosure(false)

  const renderMeeting = (m: IMeetingListItem, showDate: boolean): React.JSX.Element => {
    const canManage = isOrgAdmin || m.organizer_email.toLowerCase() === userEmail
    const shared = {
      meeting: m,
      variant,
      showDate,
      canManage,
      canDeletePermanently: isOrgAdmin,
      onAction: onCardAction,
      processing: processingById?.get(m.id) ?? null,
    }
    if (listLayout === "list") return <MeetingListRow key={m.id} {...shared} />
    return <MeetingCard key={m.id} {...shared} />
  }

  const layoutToggle = (
    <Button.Group aria-label={i18n._(msg`Meeting layout`)}>
      {(["cards", "list"] as const satisfies readonly TListLayout[]).map((layout) => (
        <Button
          key={layout}
          size="xs"
          variant={listLayout === layout ? "filled" : "default"}
          aria-pressed={listLayout === layout}
          onClick={() => onListLayoutChange?.(layout)}
        >
          {layout === "cards" ? i18n._(msg`Cards`) : i18n._(msg`List`)}
        </Button>
      ))}
    </Button.Group>
  )

  const meetingLayoutClass = listLayout === "list" ? classes.list : classes.grid

  // Lazy initialisers (run once) keep Date reads out of the render path — the
  // React Compiler rejects an impure `new Date()` evaluated on every render.
  // The cockpit greeting only needs the hour + day at mount, not per-tick.
  const [greetingHour] = useState<number>(() => new Date().getHours())
  const [today] = useState<Date>(() => new Date())
  // Weekday + short date for the cockpit's quiet line, formatted via the app
  // locale (deterministic across runtimes — see lib/format-date).
  const dateLabel = `${new Intl.DateTimeFormat(i18n.locale, { weekday: "long" }).format(today)} ${formatShortDate(i18n, today)}`
  // Compact recorded-time label for the stat chip: minutes under an hour,
  // "{h}h {m}m" beyond (mono digits keep it tidy at any magnitude).
  const recordedLabel =
    totalMinutes >= 60 ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m` : `${totalMinutes}m`

  const isLite = variant === "lite"

  // The Lexicanon toolbar's grouping tabs mirror the exact condition under
  // which the grouped body renders (not searching, list non-empty). Lite
  // keeps a single date grouping with no tabs.
  const showGroupTabs = !isLite && !momentsActive && !filteredIsEmpty

  // The "Filters" toggle only makes sense once a content search is running —
  // filters refine the Moments results. A badge surfaces the active count.
  // Lite has no Moments search, so the toggle stays off.
  const filtersButton =
    !isLite && momentsActive && filtersContent ? (
      <Button
        variant={filtersOpen ? "light" : "default"}
        onClick={filtersControl.toggle}
        leftSection={<IconFilter size={14} />}
        rightSection={
          activeFilterCount > 0 ? (
            <Badge size="xs" circle variant="filled" color="brand">
              {activeFilterCount}
            </Badge>
          ) : null
        }
        aria-expanded={filtersOpen}
        px={{ base: "xs", sm: "md" }}
      >
        <Trans>Filters</Trans>
      </Button>
    ) : null

  if (isLoading) {
    return (
      <div className={classes.page}>
        <PageHeaderSkeleton />
        <CardGridSkeleton count={6} />
      </div>
    )
  }

  if (error) {
    return (
      <div className={classes.page}>
        <EmptyState
          tone="danger"
          icon={<IconAlertTriangle />}
          title={<Trans>Couldn't load your {labels.meetings.toLowerCase()}</Trans>}
          description={<Trans>Check your connection and try again.</Trans>}
          action={
            <Stack gap="sm" align="center">
              {onRetry ? (
                <Button variant="default" onClick={onRetry}>
                  <Trans>Try again</Trans>
                </Button>
              ) : null}
              <ErrorDetails detail={error} />
            </Stack>
          }
        />
      </div>
    )
  }

  // ---- Voxcanon path: parchment empty state when there are no meetings ----
  if (product === "voxcanon" && totalCount === 0) {
    return (
      <div className={classes.page}>
        <div className="vox-empty-state">
          <img
            src="/voxcanon/empty-state.png"
            alt={i18n._(msg`Axiom waiting`)}
            className="vox-empty-state-image vox-float"
          />
          <Title order={3}>
            <Trans>The Chronicle Awaits</Trans>
          </Title>
          <Text c="dimmed" size="sm" maw={340} ta="center">
            <Trans>
              No sessions recorded yet. Start your first session and let Axiom preserve every story, every roll, every
              moment, canon forever.
            </Trans>
          </Text>
          <Button onClick={onNewMeeting} size="md">
            <Trans>Begin a new session</Trans>
          </Button>
        </div>
      </div>
    )
  }

  // The meetings block below the header. While a content search is active it
  // collapses to a single flat "Meetings" group (paired with "Moments" below);
  // otherwise it's the full date/status/people grouped dashboard. Computed here
  // rather than inline to keep the JSX ternary shallow.
  const meetingsBody = ((): React.JSX.Element => {
    if (filteredIsEmpty) {
      if (totalCount === 0) {
        return (
          <EmptyState
            tone="brand"
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="9" y="2.5" width="6" height="11" rx="3" />
                <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
                <path d="M12 17.5V21" />
                <path d="M8.5 21h7" />
                <path d="M3 9.5c-.7 1.7-.7 3.3 0 5" opacity={0.4} />
                <path d="M21 9.5c.7 1.7.7 3.3 0 5" opacity={0.4} />
              </svg>
            }
            title={<Trans>No {labels.meetings.toLowerCase()} yet</Trans>}
            description={
              <Trans>
                Start your first {labels.meetings.toLowerCase()} — it's transcribed, summarised, and turned into
                decisions and action items automatically.
              </Trans>
            }
            action={
              <Button
                onClick={onNewMeeting}
                leftSection={<IconPlayerRecord size={16} />}
                loading={isStarting}
                data-testid={isLite ? "start-recording-empty" : "start-meeting-empty"}
              >
                {isLite ? <Trans>Start recording</Trans> : <Trans>Start meeting</Trans>}
              </Button>
            }
          />
        )
      }
      // Content search running: a big dead-end empty above live Moments reads as
      // broken. Show a compact "Meetings (0)" note; the Moments section carries
      // the results.
      if (momentsActive) {
        return (
          <>
            <div className={classes.section}>
              <div className={`${classes.sectionLabel} lex-eyebrow`}>{labels.meetings}</div>
              <div className={classes.sectionCount}>0</div>
              <div className={classes.sectionRule} />
            </div>
            <Text size="sm" c="dimmed">
              <Trans>No {labels.meetings.toLowerCase()} match by title.</Trans>
            </Text>
          </>
        )
      }
      return (
        <EmptyState
          tone="neutral"
          icon={<IconSearch />}
          title={<Trans>No {labels.meetings.toLowerCase()} match your search</Trans>}
          description={<Trans>Try a different subject, topic, or person.</Trans>}
        />
      )
    }

    // Active search: one flat "Meetings" group (relevance-first), not the
    // date/status/people grouping — pairs with the "Moments" group below.
    if (momentsActive) {
      return (
        <>
          <div className={classes.section}>
            <div className={`${classes.sectionLabel} lex-eyebrow`}>{labels.meetings}</div>
            <div className={classes.sectionCount}>{filteredMeetings.length}</div>
            <div className={classes.sectionRule} />
          </div>
          <div className={meetingLayoutClass}>{filteredMeetings.map((m) => renderMeeting(m, true))}</div>
        </>
      )
    }

    return (
      <>
        {/* Voxcanon keeps its grouping tabs here, above the sections. Lexicanon
            hosts the same tabs in its fused header toolbar instead, so this row
            is Voxcanon-only — the Lexicanon path stays byte-identical below. */}
        {product === "voxcanon" && (
          <div className={classes.groupRow}>
            <div className={classes.groups} role="tablist" aria-label={i18n._(msg`Group meetings by`)}>
              {GROUPS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  role="tab"
                  aria-selected={group === g.id}
                  className={`${classes.groupTab} ${group === g.id ? classes.groupTabActive : ""}`}
                  onClick={() => onGroupChange(g.id)}
                >
                  {i18n._(g.labelKey)}
                </button>
              ))}
            </div>
          </div>
        )}

        {sections.map((section) => (
          <div key={section.id}>
            <div className={classes.section}>
              <div className={`${classes.sectionLabel} lex-eyebrow`}>{section.label}</div>
              <div
                className={classes.sectionCount}
                title={section.id === "older" ? i18n._(msg`Loaded so far — more appear when you scroll`) : undefined}
              >
                {section.items.length}
              </div>
              <div className={classes.sectionRule} />
            </div>
            <div className={meetingLayoutClass}>
              {section.items.map((m) => renderMeeting(m, group !== "date" || section.id !== "today"))}
            </div>
          </div>
        ))}

        {/* Infinite-scroll sentinel: the smart wrapper observes this by id and
            loads the next page as it scrolls into view. The button is the explicit
            fallback (and keyboard affordance). */}
        {hasMore && (
          <Stack gap="xs" mt="md" align="center">
            <div id={DASHBOARD_LOAD_MORE_SENTINEL_ID} aria-hidden="true" className={classes.loadMoreSentinel} />
            <Button variant="subtle" color="gray" size="sm" onClick={onLoadMore} loading={isLoadingMore}>
              <Trans>Load more</Trans>
            </Button>
          </Stack>
        )}

        <Stack gap="xs" mt="md">
          <Group gap="sm" justify="flex-end">
            {connected ? (
              <Text size="xs" c="var(--lex-text-success)">
                <Trans>Live updates connected</Trans>
              </Text>
            ) : (
              <Text size="xs" c="dimmed">
                <Trans>Live updates paused. Reconnecting…</Trans>
              </Text>
            )}
          </Group>
        </Stack>
      </>
    )
  })()

  return (
    <div className={classes.page}>
      {product === "voxcanon" ? (
        <>
          <VoxPageTitle
            eyebrow={{
              primary: i18n._(msg`Workspace`),
              secondary: connected ? i18n._(msg`Realtime`) : i18n._(msg`Offline`),
            }}
            leading={<Trans>Your</Trans>}
            accent={labels.meetings}
            subline={
              <Trans>
                Every session your tables have run. Auto-recapped, scene-detected, illustrated. Click any to revisit.
              </Trans>
            }
            actions={
              <>
                {isOrgAdmin && (
                  <Switch
                    label={i18n._(msg`Show hidden`)}
                    size="xs"
                    checked={showHidden}
                    onChange={(e) => onShowHiddenChange(e.currentTarget.checked)}
                  />
                )}
                <TextInput
                  id={DASHBOARD_SEARCH_INPUT_ID}
                  className={classes.searchInput}
                  value={search}
                  onChange={(e) => onSearchChange(e.currentTarget.value)}
                  placeholder={i18n._(msg`Search by subject, topic, or person…`)}
                  aria-label={i18n._(msg`Search meetings`)}
                  leftSection={<IconSearch size={14} />}
                  size="sm"
                />
                {filtersButton}
                {layoutToggle}
                <VoxPillButton variant="arcane" leading="+" onClick={onNewMeeting}>
                  <Trans>Begin session</Trans>
                </VoxPillButton>
              </>
            }
          />
          <div className={classes.sub} style={{ marginBottom: "var(--mantine-spacing-md)" }}>
            <span>
              <Trans>{totalCount} sessions</Trans>
            </span>
            {live && (
              <>
                <span className="lex-dot" />
                <span>
                  <Trans>1 recording now</Trans>
                </span>
              </>
            )}
            {actionsCount > 0 && (
              <>
                <span className="lex-dot" />
                <span>
                  <Trans>{actionsCount} open actions</Trans>
                </span>
              </>
            )}
          </div>
        </>
      ) : (
        <header className={classes.lexHeader}>
          {/* Hero cockpit — greeting, at-a-glance stats and the primary CTA on
              the same rose-wash callout surface as ReportOfferBanner. */}
          <section className={classes.cockpit}>
            <div className={classes.cockpitMain}>
              <h1 className={classes.greeting}>
                {greetingHour < 12 ? (
                  firstName ? (
                    <Trans>
                      Good morning, <span className={classes.greetName}>{firstName}</span>
                    </Trans>
                  ) : (
                    <Trans>Good morning</Trans>
                  )
                ) : greetingHour < 18 ? (
                  firstName ? (
                    <Trans>
                      Good afternoon, <span className={classes.greetName}>{firstName}</span>
                    </Trans>
                  ) : (
                    <Trans>Good afternoon</Trans>
                  )
                ) : firstName ? (
                  <Trans>
                    Good evening, <span className={classes.greetName}>{firstName}</span>
                  </Trans>
                ) : (
                  <Trans>Good evening</Trans>
                )}
              </h1>
              <div className={classes.cockpitMeta}>
                <span>
                  <Trans>
                    {dateLabel} · {monthCount} meetings this month
                  </Trans>
                </span>
                {live && (
                  <>
                    <span className={classes.liveDot} aria-hidden="true" />
                    <span>
                      <Trans>1 recording now</Trans>
                    </span>
                  </>
                )}
              </div>
            </div>

            {!isLite && (
              <div className={classes.cockpitStats}>
                <div className={classes.statChip}>
                  <span className={classes.statNum}>{totalCount}</span>
                  <span className={`${classes.statLabel} lex-eyebrow`}>
                    <Trans>sessions</Trans>
                  </span>
                </div>
                <div className={classes.statChip}>
                  <span className={classes.statNum}>{recordedLabel}</span>
                  <span className={`${classes.statLabel} lex-eyebrow`}>
                    <Trans>recorded</Trans>
                  </span>
                </div>
              </div>
            )}

            <div className={classes.cockpitCta}>
              <Button
                color="brand"
                size="md"
                className={classes.startCta}
                leftSection={<IconMicrophone size={18} />}
                onClick={onNewMeeting}
                loading={isStarting}
                data-testid={isLite ? "start-recording" : "start-meeting"}
              >
                {isLite ? <Trans>Start recording</Trans> : <Trans>Start meeting</Trans>}
              </Button>
              <Text size="xs" className={classes.ctaHint}>
                {isLite ? (
                  <Trans>Starts a Lite recording — transcript, speakers, export. No summary.</Trans>
                ) : (
                  <Trans>Starts a Full meeting — summary, decisions, action items.</Trans>
                )}
              </Text>
            </div>
          </section>

          {/* Fused toolbar — grouping, search, merge and the hidden toggle on a
              single bar directly below the cockpit. */}
          <div className={classes.toolbar}>
            {showGroupTabs && (
              <div className={classes.groups} data-bare role="tablist" aria-label={i18n._(msg`Group meetings by`)}>
                {GROUPS.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    role="tab"
                    aria-selected={group === g.id}
                    className={`${classes.groupTab} ${group === g.id ? classes.groupTabActive : ""}`}
                    onClick={() => onGroupChange(g.id)}
                  >
                    {i18n._(g.labelKey)}
                  </button>
                ))}
              </div>
            )}
            <TextInput
              id={DASHBOARD_SEARCH_INPUT_ID}
              classNames={{ root: classes.toolbarSearchRoot, input: classes.toolbarSearchInput }}
              value={search}
              onChange={(e) => onSearchChange(e.currentTarget.value)}
              placeholder={i18n._(msg`Search by subject, topic, or person…`)}
              aria-label={i18n._(msg`Search meetings`)}
              leftSection={<IconSearch size={14} />}
              size="sm"
            />
            {product === "lexicanon" && onRecordingModeFilterChange ? (
              <Button.Group>
                {RECORDING_FILTERS.map((f) => (
                  <Button
                    key={f.id}
                    size="xs"
                    variant={recordingModeFilter === f.id ? "filled" : "default"}
                    onClick={() => onRecordingModeFilterChange(f.id)}
                    aria-pressed={recordingModeFilter === f.id}
                  >
                    {i18n._(f.labelKey)}
                  </Button>
                ))}
              </Button.Group>
            ) : null}
            {layoutToggle}
            {filtersButton}
            {!isLite && (
              <Button
                variant="subtle"
                color="gray"
                className={classes.mergeBtn}
                leftSection={<IconArrowsJoin size={14} />}
                onClick={onMerge}
              >
                <Trans>Merge</Trans>
              </Button>
            )}
          </div>
        </header>
      )}

      {momentsActive && filtersContent ? (
        <Collapse in={filtersOpen}>
          <div className={classes.filtersPanel}>{filtersContent}</div>
        </Collapse>
      ) : null}

      {live &&
        (product === "voxcanon" ? (
          <VoxLiveHero
            eyebrowPrimary={i18n._(msg`Recording now`)}
            title={displayMeetingTitle(live.subject, live.topics, i18n, {
              lite: isLite || isLiteTemplate(live.template_id),
              startedAt: live.start_time,
            })}
            stats={[
              <span key="elapsed" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <IconClock size={13} />
                <Trans>{minutesSince(live.start_time)}m elapsed</Trans>
              </span>,
              (live.speaker_names?.length ?? 0) > 0 ? (
                <span key="speakers">
                  <Trans>{live.speaker_names?.length ?? 0} present</Trans>
                </span>
              ) : null,
              live.source === "teams" ? (
                <span key="source" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <IconBrandTeams size={13} />
                  Microsoft Teams
                </span>
              ) : null,
            ]}
            openLabel={i18n._(msg`Open table`)}
            onOpen={() => onOpenLive(live.id)}
          />
        ) : (
          <MeetingHeroLive meeting={live} onView={() => onOpenLive(live.id)} />
        ))}

      {recurringSeriesCard}

      {recordingModeFilter === "error" && filteredMeetings.length > 0 && onRetryAllFailed && onDeleteAllFailed ? (
        <div className={classes.failedActions}>
          <Button size="sm" onClick={onRetryAllFailed} loading={retryAllBusy}>
            <Trans>Try again for all</Trans>
          </Button>
          <Button size="sm" variant="light" color="danger" onClick={onDeleteAllFailed} disabled={retryAllBusy}>
            <Trans>Delete all…</Trans>
          </Button>
        </div>
      ) : null}

      {meetingsBody}

      {momentsActive ? momentsSection : null}
    </div>
  )
}
